import { apiRequest } from '../config/api';

export interface Carga {
  id: string;
  codigo: string;
  solicitacao_id?: string;
  placa?: string;
  motorista?: string;
  status: 'Não chegou' | 'Chegou' | 'Pronta' | 'Descarregando' | 'Finalizado' | 'Cancelado';
  filial?: string;
  data_programada?: string;
  peso?: number;
  volume?: number;
  observacoes?: string;
  fornecedor?: string;
  ordem_chegada?: number;
  hora_ordem_chegada?: string;
  prioridade?: number;
  ocorrencia?: string;
  arquivo_ocorrencia?: string;
  created_at: string;
  updated_at: string;
  solicitacoes_agendamento?: {
    id: string;
    codigo?: string;
    fornecedor?: string;
    fornecedor_mercadoria?: string;
    mercadoria_descricao?: string;
    mercadoria_a_enviar?: string;
    tipo_produto?: string;
    tipo_carga?: string;
    peso?: number;
    volume?: number;
    data_desejada?: string;
    status?: string;
    filial?: string;
    solicitante_id?: string;
    conferente?: string;
    observacoes?: string;
    ordem_chegada?: number;
    hora_ordem_chegada?: string;
    prioridade?: number;
  };
}

class CargasServiceAPI {
  // Buscar todas as cargas
  async buscarTodas(filial?: string, dataSelecionada?: string): Promise<Carga[]> {
    const params = new URLSearchParams();
    if (filial) params.append('filial', filial);
    params.append('limit', '1000');
    if (dataSelecionada) {
      params.append('data_inicio', dataSelecionada);
      params.append('data_fim', dataSelecionada + 'T23:59:59');
    }

    const response = await apiRequest(`/cargas?${params}`);
    return response.data;
  }

  // Buscar carga por ID
  async buscarPorId(id: string): Promise<Carga | null> {
    try {
      const response = await apiRequest(`/cargas/${id}`);
      return response.data;
    } catch (error) {
      return null;
    }
  }

  // Buscar carga por código
  async buscarPorCodigo(codigo: string): Promise<Carga | null> {
    try {
      const response = await apiRequest(`/cargas?codigo=${codigo}`);
      return response.data[0] || null;
    } catch (error) {
      return null;
    }
  }

  // Buscar carga por solicitação
  async buscarPorSolicitacao(solicitacaoId: string): Promise<Carga | null> {
    try {
      const response = await apiRequest(`/cargas?solicitacao_id=${solicitacaoId}`);
      return response.data[0] || null;
    } catch (error) {
      return null;
    }
  }

  // Criar carga
  async criar(carga: Omit<Carga, 'id' | 'codigo' | 'created_at' | 'updated_at'>): Promise<Carga> {
    const response = await apiRequest('/cargas', {
      method: 'POST',
      body: JSON.stringify(carga)
    });
    return response.data;
  }

  // Criar carga a partir de solicitação
  async criarDeSolicitacao(solicitacaoId: string, dadosAdicionais?: { placa?: string; motorista?: string }): Promise<Carga> {
    const response = await apiRequest(`/cargas/de-solicitacao/${solicitacaoId}`, {
      method: 'POST',
      body: JSON.stringify({ dadosAdicionais })
    });
    return response.data;
  }

  // Atualizar carga
  async atualizar(id: string, dados: Partial<Carga>): Promise<Carga> {
    const response = await apiRequest(`/cargas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados)
    });
    return response.data;
  }

  // Atualizar status
  async atualizarStatus(id: string, novoStatus: Carga['status']): Promise<Carga> {
    const response = await apiRequest(`/cargas/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: novoStatus })
    });
    return response.data;
  }

  // Cancelar carga
  async cancelar(id: string, motivo: string): Promise<Carga> {
    const response = await apiRequest(`/cargas/${id}/cancelar`, {
      method: 'PATCH',
      body: JSON.stringify({ motivo })
    });
    return response.data;
  }

  // Deletar carga
  async deletar(id: string): Promise<void> {
    await apiRequest(`/cargas/${id}`, {
      method: 'DELETE'
    });
  }

  // Converter solicitação em carga (método específico para aprovação)
  async converterSolicitacaoEmCarga(solicitacaoId: string, dadosAdicionais?: { placa?: string; motorista?: string }): Promise<Carga> {
    try {
      // 1. Verificar se já existe carga para esta solicitação
      const cargaExistente = await this.buscarPorSolicitacao(solicitacaoId);
      
      if (cargaExistente) {
        // SÓ ATUALIZAR SE FOR A MESMA SOLICITAÇÃO
        if (cargaExistente.solicitacao_id === solicitacaoId) {
          await apiRequest(`/solicitacoes-agendamento/${solicitacaoId}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status: 'Aprovado' })
          });
          return cargaExistente;
        } else {
          // NÃO RETORNAR A CARGA ERRADA! CONTINUAR E CRIAR NOVA
        }
      }

      // 2. Buscar dados da solicitação
      const solicitacoesResponse = await apiRequest(`/solicitacoes-agendamento/${solicitacaoId}`);
      const solicitacao = solicitacoesResponse.data;

      // 3. Criar nova carga usando a API
      const novaCarga = await this.criarDeSolicitacao(solicitacaoId, dadosAdicionais);

      // 4. Atualizar status da solicitação para "Aprovado"
      await apiRequest(`/solicitacoes-agendamento/${solicitacaoId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'Aprovado' })
      });

      return novaCarga;
    } catch (error) {
      console.error('Erro ao converter solicitação em carga:', error);
      throw error;
    }
  }

  // Obter estatísticas
  async obterEstatisticas(filial?: string): Promise<{
    total: number;
    naoChegaram: number;
    chegaram: number;
    prontas: number;
    descarregando: number;
    finalizadas: number;
    cancelados: number;
  }> {
    const params = new URLSearchParams();
    if (filial) params.append('filial', filial);

    const response = await apiRequest(`/cargas?${params}`);
    const cargas = response.data;

    return {
      total: cargas.length,
      naoChegaram: cargas.filter((c: Carga) => c.status === 'Não chegou').length,
      chegaram: cargas.filter((c: Carga) => c.status === 'Chegou').length,
      prontas: cargas.filter((c: Carga) => c.status === 'Pronta').length,
      descarregando: cargas.filter((c: Carga) => c.status === 'Descarregando').length,
      finalizadas: cargas.filter((c: Carga) => c.status === 'Finalizado').length,
      cancelados: cargas.filter((c: Carga) => c.status === 'Cancelado').length
    };
  }
}

export default new CargasServiceAPI();
